import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
// frontend/src/pages/maintenance/ContractDetail.tsx

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Switch,
  Autocomplete,
} from '@mui/joy';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Plus,
  PencilSimple as Edit,
  CheckCircle,
  ClipboardText as ClipboardList,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractApi, appointmentApi } from '../../lib/maintenanceApi';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { ContractWorkflows } from './ContractWorkflows';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import { apiClient } from '../../lib/api';
import type { ChecklistMinimal } from '../../types/maintenance';
import {
  CONTRACT_STATUS_LABELS,
  FREQUENCY_LABELS,
  APPOINTMENT_STATUS_LABELS,
  getStatusColor,
} from '../../types/maintenance';
import type { MaintenanceAppointmentListItem, MaintenanceFrequency, ContractStatus } from '../../types/maintenance';

// Days between visits for each cadence.
// Mirrors MaintenanceContract.get_interval_days() on the backend.
const FREQUENCY_INTERVAL_DAYS: Record<MaintenanceFrequency, number> = {
  monthly: 30,
  quarterly: 90,
  semi_annual: 180,
  annual: 365,
  bi_annual: 730,
  custom: 365,
};

/** Format a Date as the `YYYY-MM-DD` a date input expects, in local time. */
const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Shift a `YYYY-MM-DD` date by whole days without drifting across timezones. */
const addDays = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return toDateInput(new Date(year, month - 1, day + days));
};

const ContractDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [visitPage, setVisitPage] = useState(1);
  // Fetch contract
  const { data: contract, isLoading } = useQuery({
    queryKey: ['maintenance-contract', id],
    queryFn: () => contractApi.get(Number(id)),
    enabled: !!id,
  });

  // Fetch appointments for this contract
  const { data: appointmentsResponse, error: visitsError, refetch: retryVisits } = useQuery({
    queryKey: ['maintenance-appointments', { contract: id, page: visitPage }],
    queryFn: () => appointmentApi.list({ contract: Number(id), page: visitPage }),
    enabled: !!id,
  });

  const appointments = appointmentsResponse?.results || [];

  const { data: lastAppointment } = useQuery({
    queryKey: ['maintenance-latest-appointment', id],
    queryFn: async () => (await apiClient.get<MaintenanceAppointmentListItem | null>(`/api/maintenance/contracts/${id}/latest-appointment/`)).data,
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography level="h4">Contract not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/maintenance/contracts')} variant="plain">
          <ArrowLeft />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>Contract {contract.contract_number}</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {contract.contact.first_name} {contract.contact.last_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Edit size={16} />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Edit
          </Button>
          <Chip
            size="lg"
            sx={{
              bgcolor: `${getStatusColor(contract.status)}20`,
              color: getStatusColor(contract.status),
            }}
          >
            {CONTRACT_STATUS_LABELS[contract.status]}
          </Chip>
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Main Content */}
        <Box sx={{ flex: 2, minWidth: 0 }}>
          {/* Contract Details */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Contract Details
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Frequency
                    </Typography>
                    <Typography level="body-md">
                      {FREQUENCY_LABELS[contract.effective_frequency]}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Fee per Appointment
                    </Typography>
                    <Typography level="body-md">${contract.effective_fee}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Start Date
                    </Typography>
                    <Typography level="body-md">{contract.start_date}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      End Date
                    </Typography>
                    <Typography level="body-md">{contract.end_date || 'No end date'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Auto Renew
                    </Typography>
                    <Typography level="body-md">{contract.auto_renew ? 'Yes' : 'No'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      NTE (Not To Exceed)
                    </Typography>
                    <Typography level="body-md">
                      {contract.nte ? `$${contract.nte}` : 'Not set'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Linked Checklist
                    </Typography>
                    {contract.checklist ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ClipboardList size={14} />
                        <Typography level="body-md">{contract.checklist.title}</Typography>
                        <Chip size="sm" variant="soft">
                          {contract.checklist.steps_count} steps
                        </Chip>
                      </Box>
                    ) : (
                      <Typography level="body-md" sx={{ color: 'neutral.400' }}>
                        None
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Appointments */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="title-lg">Appointments</Typography>
                <Button
                  size="sm"
                  startDecorator={<Plus size={16} />}
                  onClick={() => setIsCreateAppointmentOpen(true)}
                >
                  Schedule Appointment
                </Button>
              </Box>
              {appointments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Calendar size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    No appointments scheduled
                  </Typography>
                </Box>
              ) : (
                <ResponsiveTable rows={appointments} getRowKey={row => row.id} columns={[
                  { key: 'date', header: 'Visit', mobileVariant: 'header', render: row => <Button variant="plain" onClick={() => navigate(`/maintenance/appointments/${row.id}`)}>{row.scheduled_date}</Button> },
                  { key: 'time', header: 'Time', render: row => row.scheduled_time_start || 'Not set' },
                  { key: 'tech', header: 'Technician', render: row => row.assigned_to_name || 'Unassigned' },
                  { key: 'status', header: 'Status', render: row => APPOINTMENT_STATUS_LABELS[row.status] },
                  { key: 'duration', header: 'Duration', render: row => row.duration_minutes ? `${row.duration_minutes} min` : '—' },
                ]} />
              )}
              {visitsError && <Alert color="danger" endDecorator={<Button onClick={() => retryVisits()}>Retry</Button>}>Could not load visit history.</Alert>}
              <Stack direction="row" sx={{ gap: 1, mt: 2, flexWrap: 'wrap' }}><Button variant="outlined" disabled={visitPage === 1} onClick={() => setVisitPage(p => p - 1)}>Previous visits</Button><Typography>Page {visitPage} · {appointmentsResponse?.count || 0} visits</Typography><Button variant="outlined" disabled={!appointmentsResponse?.next} onClick={() => setVisitPage(p => p + 1)}>Next visits</Button></Stack>
            </CardContent>
          </Card>
          <ContractWorkflows contract={contract} />
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1 }}>
          {/* Customer Info */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                <User size={16} style={{ marginRight: 8 }} />
                Customer
              </Typography>
              <Typography level="body-md" sx={{ fontWeight: 600 }}>
                {contract.contact.first_name} {contract.contact.last_name}
              </Typography>
              {contract.contact.email && (
                <Typography level="body-sm">{contract.contact.email}</Typography>
              )}
              {contract.contact.phone && (
                <Typography level="body-sm">{contract.contact.phone}</Typography>
              )}
            </CardContent>
          </Card>

          {/* Property Address */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                <MapPin size={16} style={{ marginRight: 8 }} />
                Property Address
              </Typography>
              <Typography level="body-md">{contract.property_address_line1}</Typography>
              {contract.property_address_line2 && (
                <Typography level="body-md">{contract.property_address_line2}</Typography>
              )}
              <Typography level="body-md">
                {contract.property_city}, {contract.property_state} {contract.property_zip}
              </Typography>
            </CardContent>
          </Card>

          {/* Legacy Inspection Checklist (JSON array) */}
          {contract.effective_checklist.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  <CheckCircle size={16} style={{ marginRight: 8 }} />
                  Inspection Checklist
                </Typography>
                <Stack spacing={1}>
                  {contract.effective_checklist.map((item, index) => (
                    <Typography key={index} level="body-sm">
                      • {item}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      </Stack>

      {/* Create Appointment Modal */}
      <CreateAppointmentModal
        open={isCreateAppointmentOpen}
        onClose={() => setIsCreateAppointmentOpen(false)}
        contractId={contract.id}
        frequency={contract.effective_frequency}
        contractStartDate={contract.start_date}
        lastAppointmentDate={lastAppointment?.scheduled_date}
        lastTechnicianId={lastAppointment?.assigned_to}
        onCreated={(apptId) => {
          queryClient.invalidateQueries({ queryKey: ['maintenance-appointments'] });
          setIsCreateAppointmentOpen(false);
          navigate(`/maintenance/appointments/${apptId}`);
        }}
      />

      {/* Edit Contract Modal */}
      <EditContractModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        contract={contract}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['maintenance-contract', id] });
          setIsEditModalOpen(false);
        }}
      />
    </Box>
  );
};

// Create Appointment Modal
// Shape of an /api/users/ row as the technician picker uses it — same as the
// one AppointmentDetail's edit modal declares for its own copy of the picker.
interface TechnicianOption {
  id: number;
  first_name: string;
  last_name: string;
}

interface CreateAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  contractId: number;
  frequency: MaintenanceFrequency;
  contractStartDate: string;
  /** Date of the most recent appointment on this contract, if there is one. */
  lastAppointmentDate?: string;
  /** Technician who took that appointment. */
  lastTechnicianId?: number | null;
  onCreated: (id: number) => void;
}

const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
  open,
  onClose,
  contractId,
  frequency,
  contractStartDate,
  lastAppointmentDate,
  lastTechnicianId,
  onCreated,
}) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTimeStart, setScheduledTimeStart] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch technicians
  const { data: techniciansData } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      const response = await apiClient.get('/api/users/');
      return response.data as { results: TechnicianOption[] };
    },
  });

  const technicians = techniciansData?.results || [];

  // The contract already knows when the next visit is due and who took the
  // last one — both are proposed here rather than read off the table above
  // and typed back in. Both remain editable, and neither is re-seeded once
  // the user has made a choice.
  React.useEffect(() => {
    if (!open) return;

    setScheduledDate((current) => {
      if (current) return current;
      const due = lastAppointmentDate
        ? addDays(lastAppointmentDate, FREQUENCY_INTERVAL_DAYS[frequency])
        : contractStartDate;
      // A contract that has fallen behind would otherwise suggest a past date.
      const today = toDateInput(new Date());
      return due > today ? due : today;
    });
  }, [open, lastAppointmentDate, contractStartDate, frequency]);

  React.useEffect(() => {
    if (!open || selectedTechnician || !lastTechnicianId) return;
    const previous = technicians.find((technician) => technician.id === lastTechnicianId);
    if (previous) setSelectedTechnician(previous);
  }, [open, lastTechnicianId, technicians, selectedTechnician]);

  const createMutation = useMutation({
    mutationFn: () =>
      appointmentApi.create({
        contract_id: contractId,
        scheduled_date: scheduledDate,
        scheduled_time_start: scheduledTimeStart || null,
        assigned_to_id: selectedTechnician?.id ?? null,
      }),
    onSuccess: (appointment) => {
      // The modal stays mounted, so without this the next open would still be
      // holding the visit that was just booked and propose it again instead of
      // the one now due after it.
      setScheduledDate('');
      setScheduledTimeStart('');
      setSelectedTechnician(null);
      onCreated(appointment.id);
    },
  });

  const handleSubmit = async () => {
    if (!scheduledDate) return;
    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 400 }}>
        <ModalClose />
        <Typography level="h4">Schedule Appointment</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl required>
            <FormLabel>Date</FormLabel>
            <Input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Start Time (optional)</FormLabel>
            <Input
              type="time"
              value={scheduledTimeStart}
              onChange={(e) => setScheduledTimeStart(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Assigned Technician</FormLabel>
            <Autocomplete
              placeholder="Select technician..."
              options={technicians}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              value={selectedTechnician}
              onChange={(_, value) => setSelectedTechnician(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting} disabled={!scheduledDate}>
              Schedule
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

// Edit Contract Modal
interface EditContractModalProps {
  open: boolean;
  onClose: () => void;
  contract: {
    id: number;
    status: ContractStatus;
    property_address_line1: string;
    property_address_line2: string;
    property_city: string;
    property_state: string;
    property_zip: string;
    start_date: string;
    end_date: string | null;
    custom_frequency: MaintenanceFrequency | '' | null;
    custom_fee: string | null;
    auto_renew: boolean;
    nte: string | null;
    effective_frequency: MaintenanceFrequency;
    effective_fee: string;
    checklist: ChecklistMinimal | null;
  };
  onSaved: () => void;
}

interface ChecklistOption {
  id: number;
  title: string;
  description?: string;
  steps_count?: number;
}

const EditContractModal: React.FC<EditContractModalProps> = ({ open, onClose, contract, onSaved }) => {
  const [status, setStatus] = useState<ContractStatus>(contract.status);
  const [propertyAddress, setPropertyAddress] = useState({
    line1: contract.property_address_line1,
    line2: contract.property_address_line2 || '',
    city: contract.property_city,
    state: contract.property_state,
    zip: contract.property_zip,
  });
  const [startDate, setStartDate] = useState(contract.start_date);
  const [endDate, setEndDate] = useState(contract.end_date || '');
  const [frequency, setFrequency] = useState<MaintenanceFrequency>(
    (contract.custom_frequency as MaintenanceFrequency) || contract.effective_frequency
  );
  const [feePerAppointment, setFeePerAppointment] = useState(
    contract.custom_fee ?? contract.effective_fee
  );
  const [overrideFrequency, setOverrideFrequency] = useState(!!contract.custom_frequency);
  const [overrideFee, setOverrideFee] = useState(contract.custom_fee !== null);
  const [autoRenew, setAutoRenew] = useState(contract.auto_renew);
  const [nte, setNte] = useState(contract.nte || '');
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistOption | null>(
    contract.checklist ? { id: contract.checklist.id, title: contract.checklist.title, steps_count: contract.checklist.steps_count } : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch checklists
  const { data: checklistsData } = useQuery({
    queryKey: ['checklists'],
    queryFn: () => ChecklistsAPI.list(),
  });
  const checklists = checklistsData?.results || [];

  // Reset form when contract changes
  React.useEffect(() => {
    setOverrideFrequency(!!contract.custom_frequency);
    setOverrideFee(contract.custom_fee !== null);
    setStatus(contract.status);
    setPropertyAddress({
      line1: contract.property_address_line1,
      line2: contract.property_address_line2 || '',
      city: contract.property_city,
      state: contract.property_state,
      zip: contract.property_zip,
    });
    setStartDate(contract.start_date);
    setEndDate(contract.end_date || '');
    setFrequency((contract.custom_frequency as MaintenanceFrequency) || contract.effective_frequency);
    setFeePerAppointment(contract.custom_fee ?? contract.effective_fee);
    setAutoRenew(contract.auto_renew);
    setNte(contract.nte || '');
    setSelectedChecklist(
      contract.checklist ? { id: contract.checklist.id, title: contract.checklist.title, steps_count: contract.checklist.steps_count } : null
    );
  }, [contract, open]);

  const updateMutation = useMutation({
    mutationFn: () =>
      contractApi.update(contract.id, {
        status,
        property_address_line1: propertyAddress.line1,
        property_address_line2: propertyAddress.line2,
        property_city: propertyAddress.city,
        property_state: propertyAddress.state,
        property_zip: propertyAddress.zip,
        start_date: startDate,
        end_date: endDate || null,
        custom_frequency: overrideFrequency ? frequency : '',
        custom_fee: overrideFee ? feePerAppointment : null,
        auto_renew: autoRenew,
        nte: nte || null,
        checklist_id: selectedChecklist?.id ?? null,
      }),
    onSuccess: () => {
      onSaved();
    },
  });

  const handleSubmit = async () => {
    if (!propertyAddress.line1 || !startDate || !frequency || !feePerAppointment) return;
    setIsSubmitting(true);
    try {
      await updateMutation.mutateAsync();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 500, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', overflow: 'hidden' }}>
        <ModalClose />
        <DialogTitle sx={{ pr: 3, flexShrink: 0 }}>Edit Contract</DialogTitle>

        <DialogContent sx={{ minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          <Stack spacing={2} sx={{ py: 1 }}>
            <FormControl required>
              <FormLabel>Status</FormLabel>
              <Select
                value={status}
                onChange={(_, value) => setStatus(value as ContractStatus)}
              >
                {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Property Address</FormLabel>
              <AddressAutocompleteInput
                value={propertyAddress.line1}
                onChange={(value) => setPropertyAddress(previous => ({ ...previous, line1: value }))}
                onAddressSelect={(parsed) => setPropertyAddress(previous => ({ ...previous, line1: parsed.address_1, city: parsed.city, state: parsed.state, zip: parsed.zip_code }))}
              />
            </FormControl>

            <Input
              placeholder="Apt, suite, etc. (optional)"
              value={propertyAddress.line2}
              onChange={(e) => setPropertyAddress({ ...propertyAddress, line2: e.target.value })}
            />

            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              <FormControl sx={{ flex: 2, minWidth: 0 }} required>
                <FormLabel>City</FormLabel>
                <Input
                  value={propertyAddress.city}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, city: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }} required>
                <FormLabel>State</FormLabel>
                <Input
                  value={propertyAddress.state}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, state: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }} required>
                <FormLabel>ZIP</FormLabel>
                <Input
                  value={propertyAddress.zip}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, zip: e.target.value })}
                />
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <Switch checked={overrideFrequency} onChange={e => setOverrideFrequency(e.target.checked)} endDecorator="Override plan cadence" />
                <FormLabel>Frequency</FormLabel>
                <Select
                  disabled={!overrideFrequency}
                  value={frequency}
                  onChange={(_, value) => setFrequency(value as MaintenanceFrequency)}
                >
                  {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <Switch checked={overrideFee} onChange={e => setOverrideFee(e.target.checked)} endDecorator="Override plan fee" />
                <FormLabel>Fee Per Appointment</FormLabel>
                <Input
                  type="number"
                  startDecorator="$"
                  placeholder="0.00"
                  disabled={!overrideFee}
                  value={feePerAppointment}
                  onChange={(e) => setFeePerAppointment(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>NTE (Not To Exceed)</FormLabel>
              <Input
                type="number"
                startDecorator="$"
                placeholder="0.00"
                value={nte}
                onChange={(e) => setNte(e.target.value)}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                Maximum repair amount before requiring client approval
              </Typography>
            </FormControl>

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <FormLabel>Auto-Renew Contract</FormLabel>
              <Switch
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Linked Checklist (optional)</FormLabel>
              <Autocomplete
                placeholder="Select a checklist..."
                options={checklists}
                getOptionLabel={(option) => option.title}
                value={selectedChecklist}
                onChange={(_, value) => setSelectedChecklist(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                Technicians will complete this checklist during appointments
              </Typography>
            </FormControl>

          </Stack>
        </DialogContent>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexShrink: 0, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!propertyAddress.line1 || !startDate || !frequency || !feePerAppointment}
          >
            Save Changes
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default ContractDetail;
